import { useContext, useLayoutEffect, useSyncExternalStore } from "react";
import { PrimaryControlsStripOpaqueChromeContext } from "#primary-controls-strip-opaque-chrome-context";
import { useIsNavigationFocused } from "#use-is-navigation-focused";

/**
 * Claims publish from layout effects, which a web server render never runs, so the server always
 * renders without one. A Suspense boundary that hydrates after an earlier part of the page has
 * already claimed must render that same value, or its window-gap padding mismatches the server
 * HTML (which React does not patch); React re-renders with the live value right after.
 */
function getServerHasOpaqueChrome(): boolean {
  return false;
}

/**
 * Whether any focused surface currently paints opaque chrome directly beneath the toolbar, so the
 * shared header fade must stand down. Read by `NavLayout`.
 */
export function useHasPrimaryControlsStripOpaqueChrome(): boolean {
  const registry = useContext(PrimaryControlsStripOpaqueChromeContext);
  return useSyncExternalStore(registry.subscribe, registry.getCurrent, getServerHasOpaqueChrome);
}

/**
 * Declare, for as long as `isActive` holds, that this surface paints opaque chrome (a pinned
 * sticky header) right under the app toolbar — suppressing the shared `NavLayout` header fade.
 *
 * The claim releases on blur, inactivity, or unmount. Inactive tabs remain mounted, so their
 * headers must not suppress the focused tab's fade. Outside navigation, the caller is focused.
 */
export function usePrimaryControlsStripOpaqueChrome(isActive: boolean): void {
  const registry = useContext(PrimaryControlsStripOpaqueChromeContext);
  const isFocused = useIsNavigationFocused();

  useLayoutEffect(() => {
    if (!isActive || !isFocused) {
      return;
    }
    return registry.publish();
  }, [isActive, isFocused, registry]);
}
